import {
    useLayoutEffect,
    useRef,
    useState,
    type FocusEvent,
    type KeyboardEvent,
    type ReactNode,
} from "react";
// eslint-disable-next-line no-restricted-imports
import { Combobox as ComboboxBase } from "@base-ui/react/combobox";
// eslint-disable-next-line no-restricted-imports
import { Select as SelectBase } from "@base-ui/react/select";
import classNames from "classnames";
import { PickerAnnouncer } from "./PickerAnnouncement";
import { usePickerChain, usePickerLevel } from "./PickerChainContext";
import { unsupportedPickerKind, usePickerRoot } from "./PickerContext";
import { isItemActionEvent } from "./PickerItemAction";
import { PickerListElementProvider } from "./PickerListElementContext";
import {
    PickerPopupNameContext,
    renderNamedPopup,
    usePickerListName,
} from "./PickerPopupName";
import {
    preventBaseUIHandler,
    usePickerLevelKeyboard,
} from "./usePickerLevelKeyboard";
import {
    PickerRowScrollGuardProvider,
    usePickerRowScrollGuard,
} from "./usePickerRowScrollGuard";
import type { PickerPopupProps } from "./Picker.types";
import styles from "./Picker.module.scss";

/** Centers a level's selected row when its list overflows (no-op where unsupported). */
const revealSelectedRow = (popup: HTMLElement) => {
    const row = popup.querySelector('[role="option"][aria-selected="true"]');
    if (row instanceof HTMLElement && "scrollIntoView" in row) {
        row.scrollIntoView({ block: "center" });
    }
};

/**
 * Whether focus sits on an element outside every picker popup (and not on
 * the body): a dialog or drawer that a selection or an action row opened
 * and focused, which the close must not pull focus back from.
 */
const isFocusClaimedOutsidePicker = (popup: HTMLElement | null) => {
    const doc = popup?.ownerDocument ?? document;
    const active = doc.activeElement;
    if (active === null || active === doc.body) {
        return false;
    }
    return active.closest("[data-picker-popup]") === null;
};

/**
 * Pulls a start-aligned flyout level up by its 1px border and 4px row inset
 * (`--border-width` + `--space-4`), so its first row lines up with the row it
 * opened from (Figma 3831:9264). Other alignments have no row to line up.
 */
const FLYOUT_ALIGN_OFFSET = -5;

/**
 * Portal + positioner + popup surface for the active root kind. Owns the
 * `--_picker-popup-max-height` / `--_picker-popup-max-width` properties and
 * matches the trigger width unless told otherwise. Nested flyout levels fly
 * out to the right of their row and portal into the root's `container`
 * unless given their own; every popup of a cascading
 * chain carries the chain id and its depth so ancestors can tell a press in
 * a descendant level from an outside press.
 */
export const PickerPopup = ({
    children,
    side: sideProp,
    align = "start",
    sideOffset: sideOffsetProp,
    container,
    matchTriggerWidth = true,
    className,
    onPointerEnter,
    onPointerLeave,
    "aria-label": ariaLabel,
}: PickerPopupProps) => {
    const root = usePickerRoot();
    const chain = usePickerChain();
    const level = usePickerLevel();
    const handleKeyDown = usePickerLevelKeyboard();
    // A flyout level hands focus back to its row itself; Base UI's return
    // focus would otherwise land on whatever was focused before it opened.
    const isFlyoutLevel = level !== null && level.depth > 0;
    const side = sideProp ?? (isFlyoutLevel ? "right" : "bottom");
    // A level opened onto an existing selection scrolls that row into view;
    // keyed on the popup element and the selection, after layout.
    const [popupElement, setPopupElement] = useState<HTMLDivElement | null>(
        null,
    );
    const { selectedValues } = root;
    useLayoutEffect(() => {
        if (isFlyoutLevel && popupElement !== null) {
            revealSelectedRow(popupElement);
        }
    }, [isFlyoutLevel, popupElement, selectedValues]);
    // The top level returns focus unless the close came from an outside press
    // or something outside the picker already took focus (a dialog a row or
    // footer opened). Base UI reads `finalFocus` when the close starts, after
    // our `onOpenChange` has recorded the reason in the ref; the callback form
    // keeps that ordering (a plain boolean would be captured at render), and
    // it is also the only form that skips Base UI's own "focus moved
    // elsewhere" check, so that check lives here.
    const finalFocus = isFlyoutLevel
        ? false
        : () =>
              root.returnFocusOnCloseRef.current &&
              !isFocusClaimedOutsidePicker(popupElement);
    // A flyout level sits flush on its row's edge; the top level floats 4px
    // off its trigger.
    const sideOffset = sideOffsetProp ?? (isFlyoutLevel ? 0 : 4);
    const alignOffset =
        isFlyoutLevel && align === "start" ? FLYOUT_ALIGN_OFFSET : 0;
    const portalContainer = container ?? chain?.container;
    const popupClassName = classNames(
        styles.popup,
        {
            [styles.popupMatchTrigger]: matchTriggerWidth,
            [styles.popupFlyout]: isFlyoutLevel,
        },
        root.classNames.popup,
        className,
    );
    const popupProps = {
        className: popupClassName,
        "data-picker-popup": "",
        "data-picker-chain": chain?.chainId,
        "data-picker-level": level?.depth,
        ref: (element: HTMLDivElement | null) => {
            level?.registerPopup(element);
            setPopupElement(element);
        },
        // Keys and focus from a `Picker.ItemAction` belong to its control:
        // no popup navigation or typeahead, and a Combobox popup does not
        // hand the focus back to its search input.
        onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
            if (isItemActionEvent(event)) {
                preventBaseUIHandler(event);
                return;
            }
            handleKeyDown(event);
        },
        onFocus: (event: FocusEvent<HTMLDivElement>) => {
            root.onPopupFocus();
            if (isItemActionEvent(event)) {
                preventBaseUIHandler(event);
            }
        },
        onBlur: root.onPopupBlur,
        onPointerEnter,
        onPointerLeave,
        render:
            ariaLabel === undefined ? undefined : renderNamedPopup(ariaLabel),
    };
    const content = (
        <PickerPopupNameContext.Provider value={ariaLabel}>
            <PickerAnnouncer>{children}</PickerAnnouncer>
        </PickerPopupNameContext.Provider>
    );

    switch (root.kind) {
        case "combobox":
            return (
                <ComboboxBase.Portal container={portalContainer}>
                    <ComboboxBase.Positioner
                        data-base-ui-swipe-ignore
                        anchor={root.triggerAnchor ?? undefined}
                        side={side}
                        align={align}
                        sideOffset={sideOffset}
                        alignOffset={alignOffset}
                        className={styles.positioner}
                    >
                        <ComboboxBase.Popup
                            {...popupProps}
                            initialFocus={() =>
                                root.searchAutoFocusRef.current ?? false
                            }
                            finalFocus={finalFocus}
                        >
                            {content}
                        </ComboboxBase.Popup>
                    </ComboboxBase.Positioner>
                </ComboboxBase.Portal>
            );
        case "select":
            return (
                <SelectBase.Portal container={portalContainer}>
                    <SelectBase.Positioner
                        data-base-ui-swipe-ignore
                        anchor={root.triggerAnchor ?? undefined}
                        side={side}
                        align={align}
                        sideOffset={sideOffset}
                        alignOffset={alignOffset}
                        alignItemWithTrigger={false}
                        className={styles.positioner}
                    >
                        <SelectBase.Popup
                            {...popupProps}
                            finalFocus={finalFocus}
                        >
                            {content}
                        </SelectBase.Popup>
                    </SelectBase.Positioner>
                </SelectBase.Portal>
            );
        default:
            return unsupportedPickerKind(root.kind);
    }
};

export interface PickerInlineSurfaceProps {
    children: ReactNode;
    className?: string;
}

/** The popup's layout without its positioner or chrome, for roots rendered `inline`. */
export const PickerInlineSurface = ({
    children,
    className,
}: PickerInlineSurfaceProps) => {
    const root = usePickerRoot();
    const level = usePickerLevel();
    const handleKeyDown = usePickerLevelKeyboard();

    return (
        <div
            ref={(element: HTMLDivElement | null) => {
                level?.registerPopup(element);
            }}
            className={classNames(
                styles.popup,
                styles.inlinePopup,
                root.classNames.popup,
                className,
            )}
            data-picker-popup=""
            onKeyDown={handleKeyDown}
        >
            <PickerAnnouncer>{children}</PickerAnnouncer>
        </div>
    );
};

/**
 * Listbox naming: `aria-labelledby` (a visible heading, such as the attached
 * preset's title) wins over `aria-label`, which wins over the popup's
 * `aria-label`; only one attribute is set.
 */
export interface PickerListProps {
    children: ReactNode;
    className?: string;
    "aria-label"?: string;
    "aria-labelledby"?: string;
}

/**
 * Space on a focused inline list presses the highlighted row, like Enter
 * (which Base UI's list handles itself). Presses inside the list, such as a
 * section's select-all checkbox, keep their own Space.
 */
const pressHighlightedRowOnSpace = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== " " || event.target !== event.currentTarget) {
        return;
    }
    event.preventDefault();
    const row = event.currentTarget.querySelector(
        '[role="option"][data-highlighted]',
    );
    if (row instanceof HTMLElement) {
        row.click();
    }
};

/**
 * Scroll container for items, sections and the pinned footer. On an inline
 * root with no `Picker.Search` the list is the tab stop: Base UI's list
 * navigation (virtual highlight, `aria-activedescendant`) then runs on the
 * list itself, which takes ArrowUp / ArrowDown, Home / End, Enter and Space.
 */
export const PickerList = ({
    children,
    className,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
}: PickerListProps) => {
    const root = usePickerRoot();
    const nameProps = usePickerListName({ ariaLabel, ariaLabelledBy });
    const listClassName = classNames(
        styles.list,
        root.classNames.list,
        className,
    );
    const isListFocusable = root.isInline && !root.hasInlineSearch;
    const listRef = useRef<HTMLDivElement>(null);
    const guardRowScroll = usePickerRowScrollGuard(listRef);
    // Keys from a `Picker.ItemAction` belong to its control: Base UI's list
    // navigation and Enter-to-select stand aside.
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (isItemActionEvent(event)) {
            preventBaseUIHandler(event);
            return;
        }
        if (isListFocusable) {
            pressHighlightedRowOnSpace(event);
        }
    };
    const rows = (
        <PickerListElementProvider value={listRef}>
            <PickerRowScrollGuardProvider value={guardRowScroll}>
                {children}
            </PickerRowScrollGuardProvider>
        </PickerListElementProvider>
    );

    switch (root.kind) {
        case "combobox":
            return (
                <ComboboxBase.List
                    ref={listRef}
                    className={listClassName}
                    tabIndex={isListFocusable ? 0 : undefined}
                    {...nameProps}
                    onKeyDown={handleKeyDown}
                >
                    {rows}
                </ComboboxBase.List>
            );
        case "select":
            return (
                <SelectBase.List
                    ref={listRef}
                    className={listClassName}
                    {...nameProps}
                    onKeyDown={handleKeyDown}
                >
                    {rows}
                </SelectBase.List>
            );
        default:
            return unsupportedPickerKind(root.kind);
    }
};
